import { useState, useEffect } from "react";
import { Modal } from "../../ui/modal";
import { FiX, FiCheck } from "react-icons/fi";
import { CopyIcon } from "../../../icons";

interface CopyPaymentLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentLink: string;
  title?: string;
  amount?: string;
}

export function CopyPaymentLinkModal({
  isOpen,
  onClose,
  paymentLink,
  title = "",
  amount = "",
}: CopyPaymentLinkModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(paymentLink);
    setCopied(true);
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
            Copy Payment Link!
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          {title && (
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Title
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {title}
              </p>
            </div>
          )}

          {amount && (
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Amount
              </p>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {amount}
              </p>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Payment Link
            </p>
            <div className="mt-1 flex rounded-md shadow-sm">
              <div className="relative flex items-stretch flex-grow focus-within:z-10">
                <input
                  type="text"
                  readOnly
                  value={paymentLink}
                  className="focus:ring-blue-500 py-2 px-2 text-gray-900 focus:border-blue-500 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
              </div>
              <button
                onClick={handleCopy}
                className="ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-600 dark:text-white dark:hover:bg-gray-500"
              >
                {copied ? (
                  <>
                    <FiCheck className="h-5 w-5 text-green-500" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <CopyIcon className="h-5 w-5 text-gray-400" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}